import clsx from 'clsx'
import { forwardRef } from 'react'

import mainStyles from 'assets/css/main.module.scss'
import { ReactComponent as AmberCheckmark } from 'assets/icons/amber-checkmark.svg'
import { ReactComponent as BlueCheckmark } from 'assets/icons/blue_checkmark.svg'
import { ReactComponent as CheckmarkIcon } from 'assets/icons/checkmark.svg'
import { ReactComponent as DarkYellowCheckmark } from 'assets/icons/dark-yellow-checkmark.svg'
import { ReactComponent as GreenCheckmark } from 'assets/icons/green_checkmark.svg'
import { ReactComponent as RedLightCheckmark } from 'assets/icons/red-light-checkmark.svg'
import { ReactComponent as YellowCheckmark } from 'assets/icons/yellow_checkmark.svg'
import Separator from 'components/separator/Separator'

import styles from './KeyTasks.module.scss'

export interface Task {
  title: string
  description?: string
  backgroundColor: string
  footer?: {
    footerTitle: string
    actions: { text: string; icon: React.ReactNode; link: string }[]
  }
}

interface Check {
  text: string
  link?: string
}

interface Props {
  tasks: Task[]
  inputs: Check[]
  outputs: Check[]
  color?: 'red' | 'yellow' | 'blue' | 'green' | 'darkYellow' | 'redLight' | 'amber'
}

const highlights = {
  red: styles.redHighlight,
  yellow: styles.yellowHighlight,
  blue: styles.blueHighlight,
  green: styles.greenHighlight,
  darkYellow: styles.darkYellowHighlight,
  redLight: styles.redLightHighlight,
  amber: styles.amberHighlight,
}

const Checkmark = {
  red: <CheckmarkIcon />,
  yellow: <YellowCheckmark />,
  blue: <BlueCheckmark />,
  green: <GreenCheckmark />,
  darkYellow: <DarkYellowCheckmark />,
  redLight: <RedLightCheckmark />,
  amber: <AmberCheckmark />,
}

const KeyTasks = forwardRef<HTMLDivElement, Props>(({ tasks, inputs, outputs, color = 'red' }, ref) => (
  <div ref={ref} className={clsx(styles.KeyTasks, mainStyles.container)}>
    <div className={styles.header}>
      <span className="relative z-2">Key</span> <span className={clsx(highlights[color], 'relative z-1')}>Tasks</span>
    </div>
    <div className={styles.content}>
      <div className={styles.tasks}>
        <Separator text={tasks?.length > 1 ? 'Tasks' : 'Task'} />
        {!!tasks?.length && (
          <div className={styles.tasksList}>
            {tasks?.map(({ title, description, backgroundColor, footer }) => (
              <div className={styles.taskItem} style={{ backgroundColor }} key={title}>
                <div className={styles.taskContainer}>
                  <div className={clsx(styles.taskTitle, !description && styles.noDesc)}>{title}</div>
                  <div className={styles.taskDescription}>{description}</div>
                </div>
                {footer && (
                  <div className={styles.footerContainer}>
                    <div className={styles.footerTitle}>{footer.footerTitle}</div>
                    <div className={styles.footerActions}>
                      {footer.actions.map(({ text, link, icon }) => (
                        <div className={styles.footerActionItem} onClick={() => window.open(link, '_blank')}>
                          <div>{icon}</div>
                          <div className={styles.actionItemTitle}>{text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.rightColumn}>
        <div className={styles.inputs}>
          <Separator text="Inputs" />
          {!!inputs?.length &&
            inputs?.map(input => (
              <div
                className={clsx(styles.inputItem, !!input.link && 'underline cursor-pointer')}
                key={input.text}
                onClick={() => {
                  !!input.link && window.open(input.link, '_blank')
                }}
              >
                <div className={styles.inputIcon}>{Checkmark[color as keyof typeof Checkmark]}</div>
                <div className={styles.inputTitle}>{input.text}</div>
              </div>
            ))}
        </div>

        <div className={styles.outputs}>
          <Separator text={outputs?.length > 1 ? 'Outputs' : 'Output'} />
          {!!outputs?.length &&
            outputs?.map(output => (
              <div
                className={clsx(styles.outputItem, !!output.link && 'cursor-pointer underline')}
                key={output.text}
                onClick={() => !!output.link && window.open(output.link, '_blank')}
              >
                <div className={styles.outputIcon}>{Checkmark[color as keyof typeof Checkmark]}</div>
                <div className={styles.outputTitle}>{output.text}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  </div>
))

export default KeyTasks
